import { useState, useEffect } from "react";
import { Modal, Radio, Alert, Spin, Typography, notification } from "antd";
import { HddOutlined, DesktopOutlined } from "@ant-design/icons";
import service from "../../services/Service.js";
import { withLocale } from "../../l10n/index.jsx";
import "./OtaAudienceModal.scss";

const { Text } = Typography;

/**
 * Destino de un software: a quién va dirigido y, por tanto, qué se comprueba antes de
 * entregarle una actualización.
 *
 * Los equipos del parque los valida Core —registrado, terminado y con la suscripción en
 * regla— antes de dejarlos actualizar. Las aplicaciones que instala un usuario no tienen
 * equipo que validar, así que se sirven sin esa consulta.
 *
 * Sirve para dos ámbitos. Sin `version` cambia el destino del firmware entero, reescribiendo
 * todas sus versiones. Con `version` cambia solo esa, que es lo que queda declarado para
 * cuando se publique; si además es la que está en distribución, el cambio es inmediato,
 * porque el puntero del canal es lo que leen los dispositivos.
 */
function OtaAudienceModal({ open, product, channel = null, target = null, version = null, current: given = undefined, isLatest = false, onCancel, onChanged, lang }) {

  // ===== 1. ESTADOS =====
  const [audience, setAudience] = useState('device');
  const [current, setCurrent] = useState(null);
  const [ui, setUI] = useState({ loading: false, saving: false });

  // ===== 2. DERIVADOS =====
  const perVersion = Boolean(version);
  const changed = audience !== current;
  const undeclared = perVersion && current === null;

  const path = [product, channel, target, version].filter(Boolean).join('/');

  // ===== 3. HELPERS =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));

  // ===== 4. HANDLERS =====
  const closeModal = () => {
    if (ui.saving) return;
    onCancel();
  };

  const submit = async () => {
    if (!changed) {
      closeModal();
      return;
    }

    const url = perVersion
      ? `ota/versions/${product}/${channel}/${version}/audience${target ? `?target=${encodeURIComponent(target)}` : ''}`
      : `ota/firmwares/${product}/audience`;

    updateUI('saving', true);
    try {
      const res = await service.put(url, { audience });
      if (!res) return;

      if (res.data.ok === false) {
        notification["error"]({ message: lang.handle(res.data.message) });
        return;
      }

      notification["success"]({ message: lang.handle('Destino actualizado') });
      onChanged();

    } catch (err) {
      console.error("Error devuelto: " + err);
    } finally {
      updateUI('saving', false);
    }
  };

  // ===== 5. EFFECTS =====
  useEffect(() => {
    if (!open || !product) return;

    // El destino de una versión ya viene con el listado; el del firmware hay que pedirlo
    if (given !== undefined) {
      setCurrent(given);
      setAudience(given ?? 'device');
      return;
    }

    setCurrent(null);
    updateUI('loading', true);

    service.get(`ota/firmwares/${product}/audience`)
      .then(res => {
        if (res && res.data.ok !== false) {
          const value = res.data.data?.audience ?? 'device';
          setCurrent(value);
          setAudience(value);
        }
      })
      .catch(err => console.error("Error devuelto: " + err))
      .finally(() => updateUI('loading', false));
  }, [open, product, version, given]);

  // ===== 6. RENDER =====
  return (
    <Modal
      title={perVersion ? lang.handle('Destino de la versión') : lang.handle('Destino del software')}
      open={open}
      onCancel={closeModal}
      onOk={submit}
      okText={lang.handle('Guardar')}
      cancelText={lang.handle('Cancelar')}
      okButtonProps={{ disabled: ui.loading }}
      cancelButtonProps={{ disabled: ui.saving }}
      confirmLoading={ui.saving}
      maskClosable={!ui.saving}
      keyboard={!ui.saving}
      className="ota-audience"
      destroyOnClose
    >
      <div className="ota-audience__path">
        <span className="ota-audience__path-label">
          {perVersion ? lang.handle('Versión') : lang.handle('Firmware')}
        </span>
        <Text code>{path}</Text>
      </div>

      {undeclared &&
        <Text type="secondary" className="ota-audience__note">
          {lang.handle('Esta versión se publicó sin declarar destino, así que se trata como dirigida a equipos')}
        </Text>
      }

      {ui.loading
        ? <div className="ota-audience__loading"><Spin size="small" /> <Text type="secondary">{lang.handle('Consultando el destino actual')}</Text></div>
        : <Radio.Group
          className="ota-audience__options"
          value={audience}
          onChange={(e) => setAudience(e.target.value)}
          disabled={ui.saving}
        >
          <Radio value="device">
            <span className="ota-audience__option">
              <HddOutlined />
              <span>
                <strong>{lang.handle('Equipos')}</strong>
                <em>{lang.handle('Core comprueba que el equipo esté registrado y con la suscripción en regla')}</em>
              </span>
            </span>
          </Radio>

          <Radio value="user">
            <span className="ota-audience__option">
              <DesktopOutlined />
              <span>
                <strong>{lang.handle('Aplicaciones de usuario')}</strong>
                <em>{lang.handle('Se descarga sin comprobar nada en Core, porque no hay equipo que comprobar')}</em>
              </span>
            </span>
          </Radio>
        </Radio.Group>
      }

      {changed && audience === 'user' && (!perVersion || isLatest) &&
        <Alert
          type="warning"
          showIcon
          className="ota-audience__warning"
          message={lang.handle('Dejará de comprobarse la suscripción')}
          description={lang.handle('Cualquiera con un token válido podrá descargar este software, sin pasar por Core.')}
        />
      }

      {changed &&
        <Text type="secondary" className="ota-audience__note">
          {perVersion
            ? isLatest
              ? lang.handle('Es la versión en distribución, así que el cambio tiene efecto de inmediato')
              : lang.handle('No está en distribución: el cambio tendrá efecto cuando se publique')
            : lang.handle('El cambio se aplica a todas las versiones publicadas de este firmware')
          }
        </Text>
      }
    </Modal>
  );
}

export default withLocale(OtaAudienceModal);
